var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import ViewModal from "../ActivityItem/ViewModal";
import ActivityItemTypeService from "../../../Domain/ActivityItemType/ActivityItemType.service";
import useService from "../../Hooks/useService";
import ServiceResult from "../../UI/ServiceResult/ServiceResult";
import FAIcon from "../../UI/FAIcon/FAIcon";
import CustomCard from "../../UI/CustomCard/CustomCard";
import DateService from "../../../Services/Shared/Date.service";
import InfoItems from "../../UI/InfoItems/InfoItems";
import PersonWithLabel from "../Person/PersonWithLabel/PersonWithLabel";
import ProfilePicture from "../Person/ProfilePicture/ProfilePicture";
import OpportunityService from "../../../Services/Opportunity.service";
import PipelineStatusBadge from "../PipelineStatus/PipelineStatusBadge";
import SearchField from "../../Form/SearchField";
import { StageSearch } from "../Stage/StageSearch";
import { ViewModalFormFormat } from "../../UI/FormFormat/FormFormat";
import PipelineService from "../../../Services/Pipeline.service";
import { AlertInfo } from "../../UI/StandardAlerts/StandardAlerts";
import RelativeDate from "../../UI/RelativeDate/RelativeDate";
import OpportunityStageHistoryService from "../../../Services/OpportunityStageHistory.service";
import usePagedSortedTable, { TotalPageMappers, } from "../../Hooks/usePagedSortedTable";
const PAGE_SIZE = 5;
const OpportunityStageHistoryListTable = ({ opportunityId, }) => {
    const { table, dataState } = usePagedSortedTable({
        striped: true,
        hover: true,
        noDataComponent: "No stage history found",
        getData: (sort, page) => OpportunityStageHistoryService.search({
            opportunityIds: [opportunityId],
            // This will filter out the last opportunity stage history (which is a little confusing to display because
            // it just has the current stage)
            fromStageExitDateTo: new Date(),
        }, Object.assign(Object.assign({}, sort), { max: PAGE_SIZE, offset: page * PAGE_SIZE })),
        getNumPages: (result) => TotalPageMappers.EngagePagedResult(result, PAGE_SIZE),
        columns: [
            {
                title: "From",
                sortId: "OPPORTUNITY_STAGE_HISTORY_FROM_STAGE_NAME",
            },
            {
                title: "To",
                sortId: "OPPORTUNITY_STAGE_HISTORY_TO_STAGE_NAME",
            },
            {
                title: "Date",
                sortId: "OPPORTUNITY_STAGE_HISTORY_FROM_STAGE_ENTRY_DATE",
            },
            {
                title: "Moved By",
                sortId: "OPPORTUNITY_STAGE_HISTORY_MOVED_INTO_FROM_STAGE_BY_PERSON_LAST_NAME",
            },
        ],
        initialSort: {
            order: "desc",
            sort: "OPPORTUNITY_STAGE_HISTORY_FROM_STAGE_ENTRY_DATE",
        },
        rows: ({ data }) => data.map((opportunityStageHistory) => {
            var _a, _b, _c, _d;
            return [
                (_a = opportunityStageHistory.fromStage.name) !== null && _a !== void 0 ? _a : "",
                (_c = (_b = opportunityStageHistory.toStage) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : "",
                opportunityStageHistory.fromStageExitDate
                    ? DateService.displayLongWithTime(opportunityStageHistory.fromStageExitDate)
                    : "",
                (_d = opportunityStageHistory.movedIntoToStageByPerson) === null || _d === void 0 ? void 0 : _d.fullName,
            ];
        }),
    });
    return (_jsx(CustomCard, { title: "Stage History " + (dataState.data ? `(${dataState.data.total})` : ""), children: _jsx("div", { className: "card-body", children: table }) }));
};
const OpportunityViewModal = (_a) => {
    var { activityItemId } = _a, modalProps = __rest(_a, ["activityItemId"]);
    const [opportunityState, loadOpportunity, { edit }] = useService(OpportunityService.get);
    React.useEffect(() => {
        if (modalProps.isOpen) {
            loadOpportunity(activityItemId);
        }
    }, [activityItemId, loadOpportunity, modalProps.isOpen]);
    // Check for permission to edit opportunities in this pipeline
    // If this permission is missing, certain menu options shouldn't appear
    const [canEditOpportunities, loadCanEditOpportunities] = useService(PipelineService.canCurrentUserEditOpportunities);
    React.useEffect(() => {
        if (opportunityState.data) {
            loadCanEditOpportunities(opportunityState.data.stage.pipeline.id);
        }
    }, [loadCanEditOpportunities, opportunityState.data]);
    return (_jsx(ViewModal, Object.assign({}, modalProps, { activityItemTypeId: ActivityItemTypeService.OpportunityId, activityItemId: activityItemId, actions: [
            opportunityState.data &&
                canEditOpportunities.data && {
                icon: "arrows-h",
                title: "Change Stage",
                // eslint-disable-next-line react/display-name
                modalBodyAndFooter: ({ cancelAction }) => {
                    var _a;
                    return (_jsxs(ViewModalFormFormat, { onCancel: cancelAction, handleSubmit: ({ stage }) => __awaiter(void 0, void 0, void 0, function* () {
                            if (opportunityState.data) {
                                const opp = yield OpportunityService.updateStage(opportunityState.data.id, stage.id);
                                edit(opp);
                                // Go back to detail screen.
                                cancelAction();
                            }
                            else {
                                throw new Error("No opportunity id provided");
                            }
                        }), children: [opportunityState.data && (_jsx(AlertInfo, { message: _jsxs("span", { children: [opportunityState.data.subject.fullName, " is currently in ", _jsx("b", { children: (_a = opportunityState.data.stage) === null || _a === void 0 ? void 0 : _a.name }), "."] }) })), _jsx(SearchField, { label: "Stage", required: true, name: "stage", as: (provided) => (_jsx(StageSearch, Object.assign({}, provided, { notStageIds: opportunityState.data
                                        ? [opportunityState.data.stage.id]
                                        : undefined, pipelineIds: opportunityState.data
                                        ? [opportunityState.data.stage.pipeline.id]
                                        : undefined }))) })] }));
                },
            },
        ], children: _jsx(ServiceResult, { state: opportunityState, children: (opportunity) => {
                var _a;
                return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "row mb-2", children: [_jsx("div", { className: "col-7", children: _jsxs("h2", { children: [_jsx(FAIcon, { icon: ActivityItemTypeService.getIcon(ActivityItemTypeService.OpportunityId), className: `mr-2 text-${ActivityItemTypeService.getColor(ActivityItemTypeService.OpportunityId)}` }), opportunity.stage.pipeline.name] }) }), _jsx("div", { className: "col-5 d-flex align-items-center justify-content-end", children: _jsx("div", { className: "h5", children: _jsx(PipelineStatusBadge, { pipelineStatus: opportunity.stage.pipeline.pipelineStatus }) }) })] }), _jsxs("div", { className: "row", children: [_jsx("div", { className: "col-7", children: _jsx(CustomCard, { title: "Info", headerRightContent: _jsx("small", { className: "text-muted", children: DateService.displayWithTime(opportunity.createdDate) }), className: "h-100", children: _jsx("div", { className: "card-body", children: _jsx(InfoItems, { items: [
                                                    {
                                                        label: "Stage",
                                                        value: opportunity.stage.name,
                                                    },
                                                    {
                                                        label: "Time in Stage",
                                                        value: (_jsx(RelativeDate, { date: opportunity.stageUpdatedDate, compact: true })),
                                                    },
                                                    {
                                                        label: "Opportunity Source",
                                                        value: (_a = opportunity.opportunitySource) === null || _a === void 0 ? void 0 : _a.name,
                                                    },
                                                    {
                                                        label: "Pipeline Entry Date",
                                                        value: DateService.displayWithTime(opportunity.pipelineEntryDate),
                                                    },
                                                ] }) }) }) }), _jsx("div", { className: "col-5", children: _jsx(CustomCard, { title: "People", className: "h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "mb-3", children: _jsx(PersonWithLabel, { fullName: opportunity.subject.fullName, photo: _jsx(ProfilePicture, { person: opportunity.subject, showProfilePicture: true }), label: "Subject" }) }), opportunity.assignedTo && (_jsx("div", { className: "mb-3", children: _jsx(PersonWithLabel, { fullName: opportunity.assignedTo.fullName, photo: _jsx(ProfilePicture, { person: opportunity.assignedTo, showProfilePicture: true }), label: "Assigned To" }) }))] }) }) })] }), _jsx("div", { className: "row my-3", children: _jsx("div", { className: "col", children: _jsx(OpportunityStageHistoryListTable, { opportunityId: opportunity.id }) }) })] }));
            } }) })));
};
export default OpportunityViewModal;
